import React from "react";
import { useTranslation } from "react-i18next";
import AppTable from "../../components/ui/AppTable";
import ListToolbar from "../../components/ui/ListToolbar";
import AppButton from "../../components/ui/AppButton";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";

const UMFacultyList = ({
  faculties, loading, totalElements, campuses, campusFilter, onCampusFilterChange,
  searchTerm, onSearchChange, onClearFilters, onCreate, onEdit, onDelete, onReactivate,
  statusFilter, onStatusFilterChange,
}) => {
  const { t } = useTranslation("universityManager");
  const { t: tCommon } = useTranslation("common");
  const { can } = useUniAdminAuth();

  const isDeleted = statusFilter === false;
  const canFilterByCampus = can(PERMISSIONS.campus.list);

  return (
    <div className="faculty-list">
      <ListToolbar
        title={t("faculties.list.title")}
        subtitle={t("faculties.list.subtitle", { count: totalElements })}
        filterBadges={(searchTerm || campusFilter) ? (
          <>
            {searchTerm && <span className="badge bg-primary">Search: "{searchTerm}"</span>}
            {campusFilter && <span className="badge bg-info">Campus: {campuses.find((c) => c.id.toString() === campusFilter)?.nameLocal}</span>}
          </>
        ) : null}
      >
        <div className="row g-3">
          <div className={canFilterByCampus ? "col-md-5" : "col-md-8"}>
            <label className="form-label fw-medium">{t("faculties.list.searchLabel")}</label>
            <div className="input-group">
              <span className="input-group-text bg-light border-end-0"><i className="fas fa-search text-muted"></i></span>
              <input type="text" className="form-control border-start-0" placeholder={t("faculties.list.searchPlaceholder")} value={searchTerm} onChange={onSearchChange} />
              {searchTerm && <button type="button" className="btn btn-outline-danger" onClick={() => onSearchChange({ target: { value: "" } })} title={tCommon("buttons.clear")}><i className="fas fa-times"></i></button>}
            </div>
          </div>
          {canFilterByCampus && (
            <div className="col-md-3">
              <label className="form-label fw-medium">{t("faculties.list.filterByCampus")}</label>
              <select className="form-select" value={campusFilter} onChange={(e) => onCampusFilterChange(e.target.value)}>
                <option value="">{t("faculties.list.allCampuses")}</option>
                {campuses.map((campus) => <option key={campus.id} value={campus.id}>{campus.nameLocal || campus.nameEn}</option>)}
              </select>
            </div>
          )}
          <div className="col-md-4 d-flex align-items-end justify-content-md-end gap-2 flex-wrap">
            {(searchTerm || campusFilter) && (
              <AppButton variant="outline" className="btn-sm" onClick={onClearFilters}>
                <i className="fas fa-times me-1"></i>{tCommon("buttons.clearFilters")}
              </AppButton>
            )}
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-sm ${statusFilter === true ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => onStatusFilterChange(true)}
              >
                <i className="fas fa-check-circle me-1"></i>
                {t("faculties.list.filterActive")}
              </button>
              <button
                type="button"
                className={`btn btn-sm ${statusFilter === false ? "btn-danger" : "btn-outline-secondary"}`}
                onClick={() => onStatusFilterChange(false)}
              >
                <i className="fas fa-trash-alt me-1"></i>
                {t("faculties.list.filterDeleted")}
              </button>
            </div>
            {!isDeleted && can(PERMISSIONS.faculty.create) && (
              <AppButton variant="primary" className="btn-sm" onClick={onCreate}>
                <i className="fas fa-plus me-1"></i>{t("crud.addFaculty")}
              </AppButton>
            )}
          </div>
        </div>
      </ListToolbar>

      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"><span className="visually-hidden">{tCommon("actions.loading")}</span></div>
              <p className="text-muted mt-2">{t("faculties.list.loading")}</p>
            </div>
          ) : faculties.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-graduation-cap text-muted mb-3" style={{ fontSize: "3rem" }}></i>
              <h5 className="text-muted">{t("faculties.list.noFound")}</h5>
              <p className="text-muted">{(searchTerm || campusFilter) ? t("faculties.list.adjustFilters") : t("faculties.list.noneCreated")}</p>
            </div>
          ) : (
            <AppTable responsive>
              <thead>
                <tr>
                  <th className="ps-4">{t("faculties.list.columns.faculty")}</th>
                  <th>{t("faculties.list.columns.studentCount")}</th>
                  <th>{t("faculties.list.columns.departmentCount")}</th>
                  <th className="text-center pe-4">{t("crud.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {faculties.map((faculty) => (
                  <tr key={faculty.id} className="border-bottom">
                    <td className="ps-4">
                      <div className="fw-bold text-dark">{faculty.nameLocal || faculty.nameEn}</div>
                      <small className="text-muted">{faculty.nameEn}</small>
                      {faculty.campusName && <div><small className="text-muted"><i className="fas fa-building me-1"></i>{faculty.campusName}</small></div>}
                    </td>
                    <td>
                      <span className="badge bg-info bg-opacity-10 text-info fw-semibold px-2 py-1">
                        <i className="fas fa-user-graduate me-1"></i>{faculty.studentCount ?? 0}
                      </span>
                    </td>
                    <td>
                      <span className="badge bg-primary bg-opacity-10 text-primary fw-semibold px-2 py-1">
                        <i className="fas fa-sitemap me-1"></i>{faculty.departmentCount ?? 0}
                      </span>
                    </td>
                    <td className="text-center pe-4">
                      {isDeleted ? (
                        can(PERMISSIONS.faculty.update) && (
                          <AppButton
                            variant="outline"
                            className="btn-sm text-success border-success"
                            onClick={() => onReactivate && onReactivate(faculty)}
                            title={t("crud.reactivate")}
                          >
                            <i className="fas fa-undo me-1"></i>
                            {t("crud.reactivate")}
                          </AppButton>
                        )
                      ) : (
                        <div className="btn-group" role="group">
                          {can(PERMISSIONS.faculty.update) && (
                            <AppButton
                              variant="outline"
                              className="btn-sm text-primary border-primary"
                              onClick={() => onEdit && onEdit(faculty)}
                              title={tCommon("buttons.edit")}
                            >
                              <i className="fas fa-edit"></i>
                            </AppButton>
                          )}
                          {can(PERMISSIONS.faculty.delete) && (
                            <AppButton
                              variant="outline"
                              className="btn-sm text-danger border-danger"
                              onClick={() => onDelete && onDelete(faculty)}
                              title={tCommon("buttons.delete")}
                            >
                              <i className="fas fa-trash"></i>
                            </AppButton>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </AppTable>
          )}
        </div>
      </div>
    </div>
  );
};

export default UMFacultyList;
